import { useEffect, useState, useContext } from "react";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  ListItemSecondaryAction,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";

import * as api from "../../services";
import { getColor } from "../../tools";
import useInteraction from "../../UserSurveys/useInteractions";
import { useMutation } from "react-query";
import importQuestions from "../../Editor/importQuestions";
import CustomTreeView from "../../Interaction/CustomTreeView";
import AppContext from "../../appContext";

export default function PreviewSurveys({
  protocol,
  protocolDispatch,
  handleNext,
  handleClose,
}) {
  // local state
  const {
    app: {
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const [interactionKeys, setInteractionKeys] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [details, setDetails] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [select, setSelect] = useState({});
  const handleExpand = (key) => {
    if (expandedKeys.includes(key)) {
      // remove key from list
      setExpandedKeys((prev) =>
        [...prev].filter((expandedKey) => expandedKey !== key)
      );
    } else {
      // add key to list
      setExpandedKeys((prev) => [...prev, key]);
    }
  };
  // server state
  const { interactions } = useInteraction({ id: protocol.protocolId });
  const loadSurveysMutation = useMutation(
    (interactionIdList) =>
      api.getLocalInteractionList({
        clientId: protocol.protocolId,
        interactionIdList,
        projectId,
        sessionId,
      }),
    {
      onSuccess: (data) => {
        let _select = {};
        let _interactionKeys = [];
        let _questions = [];
        let _details = [];
        if (data?.interactions) {
          Object.keys(data.interactions).forEach((interactionKey) => {
            const interactionValue = data.interactions[interactionKey];
            const readSurvey = importQuestions(interactionValue);
            _interactionKeys.push(interactionKey);
            _questions.push(readSurvey.questions);
            _details.push(readSurvey.details);
            _select[interactionKey] = {};
          });
        }
        setInteractionKeys(_interactionKeys);
        setSelect(_select);
        setSurveys(_questions);
        setDetails(_details);
        protocolDispatch({
          type: "SET_PROTOCOL_LAST_SAVED",
          payload: data.lastSaved,
        });
      },
    }
  );

  const next = () => {
    // save changes to protocol
    protocolDispatch({
      type: "SET_CHANGES_JSON",
      payload: JSON.stringify(select),
    }); // goto next dialog
    handleNext();
  };

  useEffect(() => {
    if (interactions.length > 0) {
      const interactionIds = interactions.map((interaction) => interaction.id);
      loadSurveysMutation.mutate(JSON.stringify(interactionIds));
    }
  }, [interactions]);

  return (
    <Box>
      <Box display="flex" justifyContent={"space-between"}>
        <Box display="flex" sx={{ gap: 1 }} alignItems="center">
          <IconButton onClick={handleClose} size="small" sx={{ padding: 1 }}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6">{t("surveys.title")}</Typography>
        </Box>
        <Button variant="contained" onClick={next}>
          {t("common.buttons.next")}
        </Button>
      </Box>
      <Box padding={1} />
      {interactions.length !== surveys.length ? ( // loading
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={250}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {interactions.map((interaction, index) => (
            <Box key={interaction.id} paddingBottom={2}>
              <List>
                <ListItem
                  button
                  sx={{ borderRadius: 1 }}
                  onClick={() => handleExpand(interaction.id)}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getColor(interaction.id) }}>
                      <AssignmentOutlinedIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={interaction.name} secondary={""} />
                  <ListItemSecondaryAction
                    onClick={() => handleExpand(interaction.id)}
                    
                  >
                    {expandedKeys.includes(interaction.id) ? (
                      <ExpandLessOutlinedIcon />
                    ) : (
                      <ExpandMoreOutlinedIcon />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
              {expandedKeys.includes(interaction.id) ? (
                <Box paddingLeft={3} paddingRight={3}>
                  <CustomTreeView
                    questions={surveys[index]}
                    isCurate={details[index]?.itemExtra?.interactionView === 1}
                    variant="body2"
                    locked={false}
                    handleUpdateTree={(tree) =>
                      setSurveys((prev) => {
                        let newSurveys = [...prev];
                        newSurveys[index] = tree;
                        return newSurveys;
                      })
                    }
                    handleSelect={(position, checked) => {
                      //let pos = "0.";
                      let pos = "";
                      let positionArr = position.split(".");
                      positionArr.pop();
                      positionArr.forEach((el, i) => {
                        if (i === positionArr.length - 1) {
                          pos = pos + (Number(el) - 1) + "";
                        } else {
                          pos = pos + (Number(el) - 1) + ".";
                        }
                      });
                      setSelect((prev) => {
                        let newChanged = { ...prev };
                        if (
                          newChanged[interactionKeys[index]][pos] === 0 ||
                          newChanged[interactionKeys[index]][pos] === 1
                        ) {
                          // remove position
                          delete newChanged[interactionKeys[index]][pos];
                        } else {
                          // add position
                          newChanged[interactionKeys[index]][pos] =
                            Number(checked);
                        }
                        return newChanged;
                      });
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
